import styled from 'styled-components';
import { TextAlign } from '../provider/Typo3Provider/types';
import { media } from '../theme/base/mixins';
import { StyledDivComponent, StyledHeadingComponent } from '../types';
import { EmphasizedText } from './Text';

export interface TitleProps {
  align?: TextAlign;
}

interface TitleType {
  H1: StyledHeadingComponent<TitleProps>;
  H2: StyledHeadingComponent<TitleProps>;
  H3: StyledHeadingComponent<TitleProps>;
  H4: StyledHeadingComponent<TitleProps>;
  H5: StyledHeadingComponent<TitleProps>;
  H6: StyledHeadingComponent<TitleProps>;
  Group: StyledDivComponent;
  Card: StyledDivComponent;
  SearchItem: StyledDivComponent;
  WatchListItem: StyledDivComponent;
  MapInfo: StyledDivComponent;
  BusTypeSelect: StyledDivComponent;
  KeyVisual: StyledDivComponent;
  OverlayForm: StyledDivComponent;
  BusOffers: StyledDivComponent;
  ContentTitle: StyledDivComponent;
  Comparison: StyledDivComponent;
  Magazine: StyledHeadingComponent<TitleProps>;
}

export const Title: TitleType = {
  H1: styled.h1<TitleProps>`
    text-align: ${(props) => props.align || 'center'};
    font-size: ${(props) =>
      (props.theme.defaults.fontSize * props.theme.components.title.h1.size) /
      2}rem;
    font-weight: ${(props) =>
      props.theme.components.title.h1.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.h1.fontStyle};
    line-height: 1.15;

    ${EmphasizedText} {
      line-height: 1.15;
    }

    ${media.large`
      font-size: ${(props) =>
        props.theme.defaults.fontSize *
        props.theme.components.title.h1.size}rem;
    `};
  `,
  H2: styled.h2<TitleProps>`
    text-align: ${(props) => props.align || 'center'};
    font-size: ${(props) =>
      (props.theme.defaults.fontSize * props.theme.components.title.h2.size) /
      1.5}rem;
    font-weight: ${(props) =>
      props.theme.components.title.h2.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.h2.fontStyle};
    line-height: 1.15;

    ${EmphasizedText} {
      line-height: 1.15;
    }

    ${media.large`
      font-size: ${(props) =>
        props.theme.defaults.fontSize *
        props.theme.components.title.h2.size}rem;
    `};
  `,
  H3: styled.h3<TitleProps>`
    text-align: ${(props) => props.align || 'center'};
    font-size: ${(props) =>
      (props.theme.defaults.fontSize * props.theme.components.title.h3.size) /
      2}rem;
    font-weight: ${(props) =>
      props.theme.components.title.h3.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.h3.fontStyle};
    line-height: 1.15;

    ${EmphasizedText} {
      line-height: 1.15;
    }

    ${media.large`
    font-size: ${(props) =>
      props.theme.defaults.fontSize * props.theme.components.title.h3.size}rem;
    `};
  `,
  H4: styled.h4<TitleProps>`
    text-align: ${(props) => props.align || 'center'};
    font-size: ${(props) =>
      props.theme.defaults.fontSize * props.theme.components.title.h4.size}rem;
    font-weight: ${(props) =>
      props.theme.components.title.h4.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.h4.fontStyle};
    line-height: ${(props) =>
      props.theme.components.title.h4.lineHeight}rem;

    ${EmphasizedText} {
      line-height: ${(props) =>
        props.theme.components.title.h4.lineHeight}rem;
    }
  `,
  H5: styled.h5<TitleProps>`
    text-align: ${(props) => props.align || 'center'};
    font-size: ${(props) =>
      (props.theme.defaults.fontSize * props.theme.components.title.h5.size) /
      1.25}rem;
    font-weight: ${(props) =>
      props.theme.components.title.h5.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.h5.fontStyle};
    line-height: 1.15;

    ${EmphasizedText} {
      line-height: 1.15;
    }

    ${media.large`
      font-size: ${(props) =>
        props.theme.defaults.fontSize *
        props.theme.components.title.h5.size}rem;
    `};
  `,
  H6: styled.h6<TitleProps>`
    text-align: ${(props) => props.align || 'center'};
    font-size: ${(props) =>
      props.theme.defaults.fontSize * props.theme.components.title.h6.size}rem;
    font-weight: ${(props) =>
      props.theme.components.title.h6.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.h6.fontStyle};
    line-height: ${(props) =>
      props.theme.components.title.h6.lineHeight}rem;

    ${EmphasizedText} {
      line-height: ${(props) =>
        props.theme.components.title.h6.lineHeight}rem;
    }
  `,
  Group: styled.div`
    font-size: ${(props) =>
      (props.theme.defaults.fontSize *
        props.theme.components.title.group.size) /
      1.25}rem;
    font-weight: ${(props) =>
      props.theme.components.title.group.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.group.fontStyle};
    line-height: 1.15;

    * {
      line-height: 1.15;
    }

    ${media.large`
      font-size: ${(props) =>
        props.theme.defaults.fontSize *
        props.theme.components.title.group.size}rem;
    `};
  `,
  Card: styled.div`
    font-size: ${(props) =>
      props.theme.defaults.fontSize *
      props.theme.components.title.card.size}rem;
    font-weight: ${(props) =>
      props.theme.components.title.card.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.card.fontStyle};
    line-height: 1.15;

    ${EmphasizedText} {
      line-height: 1.15;
    }
  `,
  SearchItem: styled.div`
    font-size: ${(props) =>
      (props.theme.defaults.fontSize *
        props.theme.components.title.searchItem.size) /
      1.5}rem;
    font-weight: ${(props) =>
      props.theme.components.title.searchItem.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.searchItem.fontStyle};
    line-height: 1.15;

    * {
      line-height: 1.15;
    }

    ${media.large`
    font-size: ${(props) =>
      props.theme.defaults.fontSize *
      props.theme.components.title.searchItem.size}rem;
    `};
  `,
  WatchListItem: styled.div`
    font-size: 2rem;
    line-height: 1;
    font-weight: 500;

    ${media.large`
      font-size: 2.5rem;
    `};
  `,
  MapInfo: styled.div`
    font-size: ${(props) =>
      props.theme.defaults.fontSize *
      props.theme.components.title.mapInfo.size}rem;
    font-weight: ${(props) =>
      props.theme.components.title.mapInfo.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.mapInfo.fontStyle};
    line-height: ${(props) =>
      props.theme.components.title.mapInfo.lineHeight}rem;

    ${EmphasizedText} {
      line-height: ${(props) =>
        props.theme.components.title.mapInfo.lineHeight}rem;
    }
  `,
  BusTypeSelect: styled.div`
    font-size: ${(props) =>
      (props.theme.defaults.fontSize *
        props.theme.components.title.busTypeSelect.size) /
      1.5}rem;
    font-weight: ${(props) =>
      props.theme.components.title.busTypeSelect.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.busTypeSelect.fontStyle};
    line-height: 1.15;

    * {
      line-height: 1.15;
    }

    ${media.medium`
      font-size: ${(props) =>
        props.theme.defaults.fontSize *
        props.theme.components.title.busTypeSelect.size}rem;
    `};
  `,
  KeyVisual: styled.div`
    font-size: ${(props) =>
      (props.theme.defaults.fontSize *
        props.theme.components.title.keyvisual.size) /
      1.5}rem;
    font-weight: ${(props) =>
      props.theme.components.title.keyvisual.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.keyvisual.fontStyle};
    line-height: 1.1;

    * {
      line-height: 1.1;
    }

    ${media.medium`
      font-size: ${(props) =>
        props.theme.defaults.fontSize *
        props.theme.components.title.keyvisual.size}rem;
    `};
  `,
  OverlayForm: styled.div`
    font-size: ${(props) =>
      props.theme.defaults.fontSize *
      props.theme.components.title.overlayForm.size}rem;
    font-weight: ${(props) =>
      props.theme.components.title.overlayForm.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.overlayForm.fontStyle};
    line-height: 1;
    margin-bottom: 10px;

    ${EmphasizedText} {
      line-height: ${(props) =>
        props.theme.components.title.overlayForm.lineHeight}rem;
    }
  `,
  BusOffers: styled.div`
    font-size: ${(props) =>
      props.theme.defaults.fontSize *
      props.theme.components.title.bussOffers.size}rem;
    font-weight: ${(props) =>
      props.theme.components.title.bussOffers.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.bussOffers.fontStyle};
    line-height: ${(props) =>
      props.theme.components.title.bussOffers.lineHeight}rem;

    ${EmphasizedText} {
      line-height: ${(props) =>
        props.theme.components.title.bussOffers.lineHeight}rem;
    }
  `,
  ContentTitle: styled.div`
    font-size: 2rem;
    font-weight: ${(props) =>
      props.theme.components.title.contentTitle.fontWeight};
    font-style: ${(props) =>
      props.theme.components.title.contentTitle.fontStyle};
    line-height: 1.15;
    margin-bottom: 5px;

    * {
      line-height: 1.15;
    }

    ${media.medium`
      font-size: ${(props) =>
        props.theme.defaults.fontSize *
        props.theme.components.title.contentTitle.size}rem;
      margin-bottom: 10px
    `};
  `,
  Comparison: styled.div`
    font-size: 3rem;
    font-weight: 300;
    color: ${(props) => props.theme.colors.secondary};
    padding-bottom: 10px;
    border-bottom: 1px solid
      ${(props) => props.theme.colors.gray};
    margin-bottom: 20px;

    ${media.large`
      font-size: 5rem;
    `};
  `,
  Magazine: styled.h2<TitleProps>`
    text-align: ${(props) => props.align || 'center'};
    font-size: ${(props) =>
      (props.theme.defaults.fontSize * props.theme.components.title.h2.size) /
      1.5}rem;
    font-weight: 800;
    font-style: ${(props) =>
      props.theme.components.title.h2.fontStyle};
    line-height: 1.15;
    color: ${(props) => props.theme.colors.secondary};

    * {
      line-height: 1.15;
    }

    ${media.large`
      font-size: ${(props) =>
        (props.theme.defaults.fontSize * props.theme.components.title.h2.size) /
        1.5}rem;
    `};
  `
};
